import { useCallback, useEffect, useRef, useState } from 'react';

const POLL_MS = 4000;

/**
 * Fetch / generate / poll one AI artifact. `get` and `generate` are request functions
 * returning axios promises ({ data: { status, result, notes } }). Fetches once on mount and,
 * while the server says "processing", polls every few seconds until completed/failed.
 *
 * Returns: { status, result, notes, loading, error, generate }
 */
export function useArtifact({ get, generate: generateRequest }) {
    const [status, setStatus] = useState('not_started');
    const [result, setResult] = useState(null);
    const [notes, setNotes] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const pollRef = useRef(null);

    const fetchOnce = useCallback(async () => {
        try {
            const res = await get();
            setStatus(res.data.status);
            setResult(res.data.result);
            setNotes(res.data.notes ?? null);
            return res.data.status;
        } catch (e) {
            setError(e);
            return 'failed';
        }
    }, [get]);

    const stopPolling = () => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
    };

    const startPolling = useCallback(() => {
        stopPolling();
        pollRef.current = setInterval(async () => {
            const s = await fetchOnce();
            if (s !== 'processing') stopPolling();
        }, POLL_MS);
    }, [fetchOnce]);

    useEffect(() => {
        let active = true;
        (async () => {
            const s = await fetchOnce();
            if (active && s === 'processing') startPolling();
            if (active) setLoading(false);
        })();
        return () => {
            active = false;
            stopPolling();
        };
    }, [fetchOnce, startPolling]);

    const generate = useCallback(async () => {
        setError(null);
        setStatus('processing');
        setResult(null);
        try {
            await generateRequest();
            startPolling();
        } catch (e) {
            setError(e);
            setStatus('failed');
        }
    }, [generateRequest, startPolling]);

    return { status, result, notes, loading, error, generate };
}
